import {
  codigoOtpSchema,
  identificacaoSchema,
  pareceEmail,
  formatarDocumento,
  senhaSchema,
  type ContatoDisponivel,
  type RespostaIdentificacao,
} from '@mymaxx/shared';
import { Botao, CampoTexto } from '@mymaxx/ui';
import { ArrowLeft, Mail, MessageSquare, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { ErroApi, authApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

/**
 * Entrada do cliente na conta (plano §5.1). Um passo de cada vez:
 *
 *   documento → [tem conta? senha ou código] → contatos mascarados → código → senha → dentro
 *
 * O cliente nunca digita para onde o código vai: os contatos vêm do cadastro no ERP.
 * Atendimento sem login não passa por aqui: quem só quer falar com alguém vai direto para
 * `/atendimento`, e o botão no fim do formulário leva para lá sem precisar voltar.
 */
type Passo =
  | { nome: 'documento' }
  | { nome: 'senha'; ident: RespostaIdentificacao }
  | { nome: 'contato'; ident: RespostaIdentificacao; motivo: 'criar' | 'entrar' }
  | {
      nome: 'codigo';
      ident: RespostaIdentificacao;
      contato: ContatoDisponivel;
      motivo: 'criar' | 'entrar';
    }
  | { nome: 'senha-nova'; tokenVerificado: string; titulo: string };

export function Entrar() {
  const { accessToken, definir } = useSessao();
  const navegar = useNavigate();
  const [passo, setPasso] = useState<Passo>({ nome: 'documento' });
  const [documento, setDocumento] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (accessToken) return <Navigate to="/conta" replace />;

  /** Executa uma chamada tratando erro e estado de envio de forma uniforme. */
  async function acao(fn: () => Promise<void>) {
    setErro(null);
    setEnviando(true);
    try {
      await fn();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível concluir. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  const enviarDocumento = (e: FormEvent) => {
    e.preventDefault();
    // Aceita documento OU e-mail: o técnico de liberação entra por este mesmo campo,
    // digitando o e-mail dele. A tela não anuncia isso — decisão de 26/09.
    const parse = identificacaoSchema.safeParse(documento);
    if (!parse.success) return setErro(parse.error.issues[0]?.message ?? 'Documento inválido');
    void acao(async () => {
      const ident = await authApi.identificar(parse.data);
      setPasso(
        ident.temConta ? { nome: 'senha', ident } : { nome: 'contato', ident, motivo: 'criar' },
      );
    });
  };

  const entrarComSenha = (e: FormEvent) => {
    e.preventDefault();
    void acao(async () => {
      definir(await authApi.login(documento, senha));
      navegar('/conta', { replace: true });
    });
  };

  const escolherContato = (
    ident: RespostaIdentificacao,
    contato: ContatoDisponivel,
    motivo: 'criar' | 'entrar',
  ) =>
    acao(async () => {
      await authApi.enviarCodigo(ident.tokenIdentificacao, contato.id);
      setCodigo('');
      setPasso({ nome: 'codigo', ident, contato, motivo });
    });

  const enviarCodigo = (e: FormEvent) => {
    e.preventDefault();
    if (passo.nome !== 'codigo') return;
    const parse = codigoOtpSchema.safeParse(codigo);
    if (!parse.success) return setErro(parse.error.issues[0]?.message ?? 'Código inválido');
    void acao(async () => {
      const r = await authApi.validarCodigo(passo.ident.tokenIdentificacao, parse.data);
      if (passo.motivo === 'criar') {
        setPasso({
          nome: 'senha-nova',
          tokenVerificado: r.tokenVerificado,
          titulo: 'Crie sua senha',
        });
      } else if (r.temConta) {
        definir(await authApi.entrarComCodigo(r.tokenVerificado));
        navegar('/conta', { replace: true });
      } else {
        setPasso({
          nome: 'senha-nova',
          tokenVerificado: r.tokenVerificado,
          titulo: 'Crie sua senha',
        });
      }
    });
  };

  const salvarSenha = (e: FormEvent) => {
    e.preventDefault();
    if (passo.nome !== 'senha-nova') return;
    const parse = senhaSchema.safeParse(senha);
    if (!parse.success) return setErro(parse.error.issues[0]?.message ?? 'Senha inválida');
    void acao(async () => {
      const sessao =
        passo.titulo === 'Crie sua senha'
          ? await authApi.criarConta(passo.tokenVerificado, parse.data).catch(async (e) => {
              // Conta criada por outro caminho no meio do fluxo: redefine em vez de falhar.
              if (e instanceof ErroApi && e.status === 400 && /já tem acesso/.test(e.mensagem)) {
                return authApi.redefinirSenha(passo.tokenVerificado, parse.data);
              }
              throw e;
            })
          : await authApi.redefinirSenha(passo.tokenVerificado, parse.data);
      definir(sessao);
      navegar('/conta', { replace: true });
    });
  };

  const voltar = () => {
    setErro(null);
    setSenha('');
    setPasso({ nome: 'documento' });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <header className="mb-6">
        <p className="text-sm font-semibold text-marca-600">MyMAXX</p>
        <h1 className="text-xl font-semibold">
          {passo.nome === 'documento' && 'Acesse sua conta'}
          {passo.nome === 'senha' && `Olá, ${passo.ident.primeiroNome}!`}
          {passo.nome === 'contato' && 'Como quer receber o código?'}
          {passo.nome === 'codigo' && 'Digite o código'}
          {passo.nome === 'senha-nova' && passo.titulo}
        </h1>
      </header>

      {passo.nome === 'documento' && (
        <form className="space-y-4" onSubmit={enviarDocumento} noValidate>
          {/* Teclado normal, e não numérico: este campo também recebe o e-mail do técnico
              de liberação, e o teclado numérico do celular não tem letras — ele ficava
              impedido de entrar. O cliente paga um toque a mais no "123"; o técnico
              passava a não conseguir usar o sistema pelo telefone. */}
          <CampoTexto
            rotulo="CPF ou CNPJ"
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={documento}
            onChange={(e) => setDocumento(e.target.value)}
            // Só formata o que é documento: formatar um e-mail o destruiria.
            onBlur={() => setDocumento((d) => (pareceEmail(d) ? d.trim() : formatarDocumento(d)))}
            ajuda="Use o documento do titular do contrato."
          />
          <Mensagem erro={erro} />
          <Botao type="submit" className="w-full" carregando={enviando}>
            Continuar
          </Botao>
          {/* Quem chegou aqui pode não precisar de conta nenhuma: falar com um atendente
              não exige login. O caminho fica como botão, e não como aviso, para não
              obrigar a voltar e clicar de novo na tela anterior. */}
          <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
            <p className="text-center text-xs text-neutral-500">
              Precisa só falar com um atendente? Não precisa de conta.
            </p>
            <Link to="/atendimento" className="block">
              <Botao variante="secundario" className="w-full">
                Iniciar atendimento
              </Botao>
            </Link>
          </div>
        </form>
      )}

      {passo.nome === 'senha' && (
        <form className="space-y-4" onSubmit={entrarComSenha} noValidate>
          <CampoTexto
            rotulo="Senha"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
          <Mensagem erro={erro} />
          <Botao type="submit" className="w-full" carregando={enviando}>
            Entrar
          </Botao>
          {/* O técnico não tem contato no ERP para receber código: para ele, a saída é
              pedir uma senha nova ao admin. Mostrar o atalho abriria um caminho sem fim. */}
          {!passo.ident.pedirSenha && (
            <button
              type="button"
              className="text-marca-600 w-full text-center text-sm underline"
              onClick={() => setPasso({ nome: 'contato', ident: passo.ident, motivo: 'entrar' })}
            >
              Esqueci minha senha / entrar com código
            </button>
          )}
          <Voltar onClick={voltar} />
        </form>
      )}

      {passo.nome === 'contato' && (
        <div className="space-y-3">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Enviaremos um código para o contato cadastrado na MAXX Conectado. Não reconhece nenhum?
            Fale com nosso atendimento para atualizar seus dados.
          </p>
          {passo.ident.contatos.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={enviando}
              onClick={() => void escolherContato(passo.ident, c, passo.motivo)}
              className="flex w-full items-center gap-3 rounded-card border border-neutral-200 p-4 text-left transition hover:border-marca-400 disabled:opacity-60 dark:border-neutral-800"
            >
              {c.canal === 'sms' ? (
                <MessageSquare className="text-marca-600" size={20} />
              ) : (
                <Mail className="text-marca-600" size={20} />
              )}
              <span>
                <span className="block text-sm font-medium">
                  {c.canal === 'sms' ? 'SMS' : 'E-mail'}
                </span>
                <span className="block text-sm text-neutral-500">{c.valor}</span>
              </span>
            </button>
          ))}
          <Mensagem erro={erro} />
          <Voltar onClick={voltar} />
        </div>
      )}

      {passo.nome === 'codigo' && (
        <form className="space-y-4" onSubmit={enviarCodigo} noValidate>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Enviamos um código de 6 dígitos para <strong>{passo.contato.valor}</strong>.
          </p>
          <CampoTexto
            rotulo="Código"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={7}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
          />
          <Mensagem erro={erro} />
          <Botao type="submit" className="w-full" carregando={enviando}>
            Confirmar
          </Botao>
          <button
            type="button"
            className="w-full text-center text-sm text-marca-600 underline"
            onClick={() => void escolherContato(passo.ident, passo.contato, passo.motivo)}
          >
            Reenviar código
          </button>
          <Voltar onClick={voltar} />
        </form>
      )}

      {passo.nome === 'senha-nova' && (
        <form className="space-y-4" onSubmit={salvarSenha} noValidate>
          <div className="flex items-start gap-2 rounded-lg bg-marca-50 p-3 text-xs text-marca-900 dark:bg-marca-950 dark:text-marca-100">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>
              Ao criar sua conta você concorda com o{' '}
              <Link to="/privacidade" className="underline">
                aviso de privacidade
              </Link>
              . Seus dados são usados apenas para o atendimento e você pode pedir exportação ou
              exclusão a qualquer momento.
            </span>
          </div>
          <CampoTexto
            rotulo="Nova senha"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            ajuda="Mínimo de 10 caracteres. Evite senhas usadas em outros sites."
          />
          <Mensagem erro={erro} />
          <Botao type="submit" className="w-full" carregando={enviando}>
            Salvar e entrar
          </Botao>
          <Voltar onClick={voltar} />
        </form>
      )}
    </main>
  );
}

function Mensagem({ erro }: { erro: string | null }) {
  if (!erro) return null;
  return (
    <p role="alert" className="text-sm text-red-600">
      {erro}
    </p>
  );
}

function Voltar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mx-auto flex items-center gap-1 text-xs text-neutral-500 underline"
    >
      <ArrowLeft size={12} /> Começar de novo
    </button>
  );
}
